<template>
  <div
    v-if="!hideBanners && !swapReviewButtonPresent"
    id="banners-toasts"
    :class="{ 'scroll-locked-banners': isScrollLocked }"
    :style="{ '--scroll-position': `${scrollPosition}px` }">
    <div
      id="banners"
      class="d-flex flex-column">
      <div
        v-if="isImpersonated && !stylistClosetInProgress"
        class="d-flex justify-content-end align-items-center card-panel text-center bg-warning p-3">
        <p>You are impersonating <b>{{ clientFullName }}</b></p>
        <a
          class="btn btn-danger px-5 mx-2"
          href="/impersonate/stop/?next=/admin/clients/client/">
          Stop
        </a>
      </div>
      <StylingBanner v-if="impersonatorIsStylist"/>
      <LoyaltyCashBanner/>
      <BaseBanner
        v-for="banner in banners"
        :key="banner.name"
        :link="banner.link"
        :type="banner.type"
        :button="banner.button"
        :show-icon="banner.showIcon"
        :show-close-button="!!banner.onClose"
        @close="banner.onClose">
        <template #title>
          {{ banner.title }}
        </template>
        <template #text>
          <div
            v-if="banner.name === 'FirstCaseTimeoutBanner'"
            class="align-self-center">
            <CountdownTimer
              use-days
              :cutoff="new Date(firstCaseDueDate)">
              <template #after>
                <b> left to fill your first case</b>
                before we automatically send you a stylist-filled case.
              </template>
            </CountdownTimer>
          </div>
          <span
            v-else>
            {{ banner.text }}
          </span>
        </template>
      </BaseBanner>
    </div>
    <!-- Toast notifications should be last, below any banners etc. -->
    <ToastNotificationList/>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import ToastNotificationList from '@/components/global/ToastNotificationList.vue'
import StylingBanner from '@/components/headers/StylingBanner.vue'
import LoyaltyCashBanner from '@/components/headers/LoyaltyCashBanner.vue'
import BaseBanner from '@/components/headers/BaseBanner.vue'
import CountdownTimer from '@/components/global/dates/CountdownTimer.vue'
import { useClientStore } from '@shared/stores/client.js'
import useRouteMetadata from '@/composables/routeMetadata.js'
import useScreenSize from '@shared/composables/screenSize.js'
import { useStylingStore } from '@/stores/styling'
import { useFedExOptInStore } from '@/stores/fedExOptIn.js'
import { useRejoinStore } from '@/stores/rejoin.js'
import { useScrollStore } from '@shared/stores/scrollLock.js'
import { useUnlimitedTrialStore } from '@/stores/unlimitedTrial.js'
import { useElementPresence } from '@/composables/element.js'

const { isMobile } = useScreenSize()
const { hideBanners } = useRouteMetadata()
const { isScrollLocked, scrollPosition } = storeToRefs(useScrollStore())
const swapReviewButtonPresent = useElementPresence('.swap-review-button')
const {
  isImpersonated,
  impersonatorIsStylist,
  firstCaseDueDate,
  name: clientFullName,
  paymentIsExpired,
  showFirstCaseCountdown
} = storeToRefs(useClientStore())

const styling = useStylingStore()

const stylistClosetInProgress = computed(() => styling.stylistClosetInProgress)

const { dismissFedExBanner } = useFedExOptInStore()
const { showFedExPickupBanner } = storeToRefs(useFedExOptInStore())

const { showRejoinBanner } = storeToRefs(useRejoinStore())
const { beginRejoinFlow, dismissRejoinBanner } = useRejoinStore()
const unlimitedTrial = useUnlimitedTrialStore()
const { showUnlimitedTrialBanner } = storeToRefs(unlimitedTrial)
const { unlimitedTrialBannerDismissed } = unlimitedTrial

const banners = computed(() => {
  const banners = []
  if (paymentIsExpired.value) {
    banners.push(
      {
        name: 'PaymentExpiredBanner',
        title: 'Your credit card has expired.',
        text: 'Please update your payment method.',
        link: {
          title: 'Update now',
          type: 'router-link',
          underline: false,
          bold: true,
          variant: 'inherit',
          to: { name: 'account-billing' }
        },
        type: 'default'
      }
    )
  }
  if (showFirstCaseCountdown.value) {
    banners.push({
      name: 'FirstCaseTimeoutBanner',
      type: 'smart'
    })
  }
  if (showFedExPickupBanner.value) {
    banners.push({
      name: 'FedExPickupBanner',
      type: 'no-line-break-primary',
      title: 'Time for your free pickup with FedEx.',
      showIcon: false,
      link: {
        title: 'Schedule Home Pickup',
        type: 'router-link',
        classes: isMobile ? 'm-auto' : '',
        underline: true,
        to: { name: 'account', query: { section: 'shipping-returns' } }
      },
      onClose() { dismissFedExBanner() }
    })
  }
  if (showRejoinBanner.value) {
    banners.push({
      name: 'RejoinBanner',
      type: 'no-line-break-primary',
      title: 'Ready to Refresh Your Wardrobe?',
      showIcon: false,
      button: {
        text: 'Rejoin',
        variant: 'secondary',
        classes: isMobile ? 'w-100' : '',
        action: () => {
          beginRejoinFlow()
        }
      },
      onClose() { dismissRejoinBanner() }
    })
  }
  if (showUnlimitedTrialBanner.value) {
    banners.push({
      name: 'UnlimitedTrialBanner',
      type: 'default-center',
      showIcon: false,
      title: 'Enjoy unlimited swaps this month.',
      text: 'Swap one item or swap them all, whenever you want!',
      onClose() { unlimitedTrialBannerDismissed() },
    })
  }

  return banners
})
</script>

<style lang="scss" scoped>
#banners-toasts {
  position: sticky;
  top: $nav-bar-height-mobile;
  z-index: $zindex-sticky;

  @include media-desktop {
    top: 0;
  }
  &.scroll-locked-banners {
    top: var(--scroll-position);
  }

  #banners {
    @include media-tablet-or-larger {
      background-color: $white;
      gap: 1px;
    }
  }
}
</style>
